import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { Schema } from 'effect';
import { Effect } from 'effect';

import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import type { Paths } from '@vs-shop/schemas/openapi/paths';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/addresses';
const HTTP_METHOD = 'post';

export type CreateAddressRequestBody = Paths[typeof ENDPOINT][typeof HTTP_METHOD] extends {
  requestBody: { content: infer Content };
}
  ? Content extends Record<string, infer Body>
    ? Body
    : never
  : never;

export type MakeCreateAddressDeps<AddressShowSchema extends Schema.Top, AddressCreateInput> = {
  readonly addressShow: AddressShowSchema;
  readonly addressCreateBody: (data: AddressCreateInput) => CreateAddressRequestBody;
  readonly addressFirstName: (data: AddressCreateInput) => string;
  readonly addressTraceFields: (data: AddressCreateInput) => {
    readonly firstName: string;
    readonly lastName: string;
  };
};

export const makeCreateAddress = <AddressShowSchema extends Schema.Top, AddressCreateInput>(
  deps: MakeCreateAddressDeps<AddressShowSchema, AddressCreateInput>
) => {
  const CreateAddressError = createBackendApiError({
    domain: 'Address',
    endpoint: 'Create',
    path: ENDPOINT,
    identifierField: 'firstName',
  });

  const postAddressFetch = (data: AddressCreateInput) => {
    const traceFields = deps.addressTraceFields(data);
    return Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/json', 'application/json')
        .fetch({ body: deps.addressCreateBody(data) });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'address.firstName': traceFields.firstName,
          'address.lastName': traceFields.lastName,
        },
      })
    );
  };

  const createAddress = (data: AddressCreateInput) => {
    const traceFields = deps.addressTraceFields(data);
    return postAddressFetch(data).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.addressShow)(response),
          422: CreateAddressError.createViolationError(deps.addressFirstName(data)),
          orElse: (response) =>
            Effect.fail(
              new CreateAddressError.ApiRequestError({
                firstName: deps.addressFirstName(data),
                response,
              })
            ),
        })
      ),
      Effect.tap(() =>
        logApiOperation(
          ApiModule.AddressCreate,
          'Debug',
          `Successfully created address for ${traceFields.firstName} ${traceFields.lastName}`
        )
      ),
      Effect.withSpan('api.address.create', {
        kind: 'internal',
        attributes: {
          'address.firstName': traceFields.firstName,
          'address.lastName': traceFields.lastName,
        },
      })
    );
  };

  return { CreateAddressError, createAddress };
};
